/**
 * Firebase configuration and initialization
 * Initializes Firebase Auth, Firestore, and Storage
 *
 * @module lib/firebase
 */

import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import { getAuth, connectAuthEmulator, GoogleAuthProvider, type Auth } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator, type Firestore } from 'firebase/firestore';
import { getStorage, connectStorageEmulator, type FirebaseStorage } from 'firebase/storage';
import { getAnalytics, type Analytics } from 'firebase/analytics';
import { getFunctions, connectFunctionsEmulator, type Functions } from 'firebase/functions';
import { logError } from './sentry';

/**
 * Validate required Firebase environment variables
 * Throws clear error if any required variables are missing
 *
 * This validation ensures the app fails fast with a helpful error message
 * instead of encountering cryptic Firebase errors later.
 */
function validateFirebaseConfig(): void {
  // Required environment variables for Firebase core functionality
  const requiredEnvVars = {
    VITE_FIREBASE_API_KEY: import.meta.env.VITE_FIREBASE_API_KEY,
    VITE_FIREBASE_AUTH_DOMAIN: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    VITE_FIREBASE_PROJECT_ID: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    VITE_FIREBASE_STORAGE_BUCKET: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    VITE_FIREBASE_MESSAGING_SENDER_ID: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    VITE_FIREBASE_APP_ID: import.meta.env.VITE_FIREBASE_APP_ID,
    // Note: VITE_FIREBASE_MEASUREMENT_ID is optional - Analytics will be disabled if not set
  };

  const missingVars = Object.entries(requiredEnvVars)
    .filter(([_, value]) => !value)
    .map(([key]) => key);

  if (missingVars.length > 0) {
    const errorMessage = [
      '❌ Firebase Configuration Error',
      '',
      'Missing required environment variables:',
      ...missingVars.map((varName) => `  - ${varName}`),
      '',
      'Please ensure apps/web/.env.local contains all required Firebase configuration values.',
      'See apps/web/.env.example for the template.',
    ].join('\n');

    throw new Error(errorMessage);
  }
}

// Validate environment variables before initializing Firebase
validateFirebaseConfig();

/**
 * Firebase configuration from environment variables
 * Set in apps/web/.env.local
 */
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

/**
 * Initialize Firebase app (singleton pattern)
 * Only initializes once, even if called multiple times
 */
let app: FirebaseApp;
if (!getApps().length) {
  app = initializeApp(firebaseConfig);
} else {
  app = getApps()[0];
}

/**
 * Firebase Authentication instance
 * Use for all auth operations (signup, login, logout)
 */
export const auth: Auth = getAuth(app);

/**
 * Google OAuth provider for social sign-in (Story 23.1)
 * Shared singleton used by the "Sign in with Google" button.
 *
 * `prompt: 'select_account'` forces Google's account chooser on every attempt,
 * so account-switching works on shared family devices instead of silently
 * reusing the last-signed-in Google account.
 */
export const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

/**
 * Firestore database instance
 * Use for reading/writing household, story, character data
 */
export const db: Firestore = getFirestore(app);

/**
 * Firebase Storage instance
 * Use for uploading/downloading photos for character creation
 */
export const storage: FirebaseStorage = getStorage(app);

/**
 * Firebase Cloud Functions instance
 * Use for calling backend functions (story generation, data export, etc.)
 * Region: europe-west1 (GDPR compliance)
 */
export const functions: Functions = getFunctions(app, 'europe-west1');

/**
 * Firebase Analytics instance
 * Use for tracking user events and behavior analytics
 *
 * Story 3.8: Analytics Event Tracking (FR52-58)
 * - Initialized client-side only (not during SSR)
 * - Tracks 10 core story creation events
 * - Household-level tracking (GDPR compliant)
 */
export let analytics: Analytics | null = null;

// Initialize Analytics only in browser (not SSR) and only if measurement ID is configured
if (typeof window !== 'undefined' && import.meta.env.VITE_FIREBASE_MEASUREMENT_ID) {
  try {
    analytics = getAnalytics(app);
    console.info('✅ Firebase Analytics initialized');
  } catch (error) {
    // Log error but don't throw - analytics is non-critical
    console.warn('⚠️ Failed to initialize Firebase Analytics:', error);
  }
} else if (typeof window !== 'undefined') {
  console.info('ℹ️ Firebase Analytics disabled (no measurement ID configured)');
}

/**
 * Connect to Firebase Emulators in development mode
 * Allows local testing without hitting production Firebase
 *
 * Emulator ports configured in firebase.json:
 * - Auth: localhost:9099
 * - Firestore: localhost:8080
 * - Storage: localhost:9199
 * - Functions: localhost:5001
 *
 * Controlled by VITE_USE_FIREBASE_EMULATORS environment variable
 */
if (import.meta.env.DEV && import.meta.env.VITE_USE_FIREBASE_EMULATORS === 'true') {
  try {
    // Only connect once (prevents "already connected" errors)
    connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
    connectFirestoreEmulator(db, 'localhost', 8080);
    connectStorageEmulator(storage, 'localhost', 9199);
    connectFunctionsEmulator(functions, 'localhost', 5001);
    console.info('🔧 Connected to Firebase Emulators');
  } catch (error) {
    // Ignore "already connected" errors on hot reload
    if (error instanceof Error && !error.message.includes('already been called')) {
      logError(error, {
        action: 'firebase_emulator_connection',
        environment: 'development',
      });
    }
  }
}

/**
 * Helper to convert Firestore Timestamp to JavaScript Date
 * Firestore stores dates as Timestamp objects { seconds, nanoseconds }
 */
export function timestampToDate(
  timestamp: { seconds: number; nanoseconds: number } | null
): Date | null {
  if (!timestamp) return null;
  return new Date(timestamp.seconds * 1000);
}

export default app;
